import type { ReactNode } from 'react';
import type { DraftDto, PublicationKind, RenderDraftStateDto } from '@app/shared';
import { useT } from '../i18n';
import { formatChips, lookPriceLine, variantOf, type Look } from '../lib/format-text';
import { haptic } from '../lib/tma';
import { HelpHint, type HelpTopic } from './HelpHint';
import { Chip } from './ui';

/** The lime dot of the advised format (spec 4.2). */
const ADVISED_DOT = 'rgb(var(--accent))';

function Row({ title, hint, children }: { title: string; hint: HelpTopic; children: ReactNode }) {
  return (
    <div className="mb-3">
      <h2 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.06em] text-hint mb-1.5">
        {title}
        <HelpHint topic={hint} />
      </h2>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

/**
 * The choice of «Формат и текст» (spec 4.2): the formats the passport offers
 * (the advised one with a lime dot), the styles of the chosen format, the
 * carousel kind and the price line. Nothing here is paid: the page stores the
 * choice, or — with text — asks before another format writes it again.
 */
export function LookPicker({
  state,
  draft,
  look,
  disabled,
  onKind,
  onStyle,
  onAnimated,
}: {
  state: RenderDraftStateDto;
  draft: Pick<DraftDto, 'format' | 'type' | 'status' | 'version'>;
  look: Look;
  disabled: boolean;
  onKind: (kind: PublicationKind) => void;
  onStyle: (style: string) => void;
  onAnimated: (animated: boolean) => void;
}) {
  const t = useT();
  const f = t.formatText;
  const chips = formatChips(state, draft);
  const variant = variantOf(state, look.kind);
  const price = lookPriceLine(state, look, t);
  const limitReached = draft.version > 0 && state.rewritesLeft === 0 && chips.some((c) => c.closed);
  const tap = (fn: () => void) => () => {
    haptic('light');
    fn();
  };

  return (
    <section className="mb-4">
      <Row title={f.format} hint="format">
        {chips.map((c) => (
          <Chip
            key={c.kind}
            selected={c.kind === look.kind}
            dot={c.advised ? ADVISED_DOT : undefined}
            disabled={disabled || c.closed}
            onClick={tap(() => c.kind !== look.kind && onKind(c.kind))}
          >
            {f.formats[c.kind]}
          </Chip>
        ))}
      </Row>
      {limitReached && <p className="text-xs text-hint -mt-1.5 mb-3">{f.rewriteLimit}</p>}

      {variant && variant.styles.length > 0 && (
        <Row title={f.style} hint="style">
          {variant.styles.map((s) => (
            <Chip
              key={s.id}
              selected={s.id === look.style}
              dot={s.accent}
              disabled={disabled}
              onClick={tap(() => s.id !== look.style && onStyle(s.id))}
            >
              {s.name}
            </Chip>
          ))}
        </Row>
      )}

      {look.kind === 'carousel' && variant && (
        <Row title={f.carousel} hint="carousel">
          <Chip selected={look.animated} disabled={disabled} onClick={tap(() => !look.animated && onAnimated(true))}>
            {f.animated(variant.price)}
          </Chip>
          <Chip selected={!look.animated} disabled={disabled} onClick={tap(() => look.animated && onAnimated(false))}>
            {f.static(variant.staticPrice ?? variant.price)}
          </Chip>
        </Row>
      )}

      {price && <p className="text-sm font-semibold">{price}</p>}
    </section>
  );
}
